import { useRef, useState } from 'react';
import { Download, Eye, FileText, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';

/** The "editor" for a file-backed (upload) working copy: editing a binary document is
 *  a native-app re-upload, so there's no in-app editor — just replace the file. Shows
 *  the working copy's current file (with view/download) and lets the owner upload a
 *  revised one. */
export function FileReplaceCard({
  filename,
  downloadUrl,
  viewUrl,
  replacing,
  onPick,
}: {
  filename: string | null;
  downloadUrl?: string | null;
  viewUrl?: string | null;
  replacing: boolean;
  onPick: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const fileLink = 'inline-flex items-center gap-1 text-[12px] font-medium text-brand-blue hover:underline';

  return (
    <div className="rounded-2xl border border-line bg-white p-6 shadow-card">
      <div className="flex items-center gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-line-soft text-muted">
          <FileText className="size-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-semibold text-navy">{filename ?? 'No file'}</p>
          <p className="text-[12.5px] text-muted">
            {picked ? `Ready to replace with "${picked}"` : 'Current working-copy file'}
          </p>
        </div>
        {!picked && (downloadUrl || viewUrl) && (
          <div className="flex shrink-0 items-center gap-3">
            {viewUrl && (
              <a href={viewUrl} target="_blank" rel="noopener" className={fileLink}>
                <Eye className="size-3.5" aria-hidden="true" /> View
              </a>
            )}
            {downloadUrl && (
              <a href={downloadUrl} target="_blank" rel="noopener" className={fileLink}>
                <Download className="size-3.5" aria-hidden="true" /> Download
              </a>
            )}
          </div>
        )}
      </div>

      {/* No `accept` filter: the vault takes any file that is not an archive, an executable or a
          macro-enabled Office file, and the server says so with a reason when it refuses. A
          picker filtered to PDF/Word/Excel hid a PNG working copy's own kind from the person
          replacing it. */}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          e.target.value = ''; // allow re-picking the same filename
          if (!f) return;
          setPicked(f.name);
          onPick(f);
        }}
      />
      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line-soft pt-4">
        <Button
          variant="outline"
          disabled={replacing}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="size-4" aria-hidden="true" />
          {replacing ? 'Uploading…' : 'Replace this document'}
        </Button>
        <p className="text-[12px] text-muted">
          Any file type the vault accepts (PDF, Word, Excel, PowerPoint, images, video, text) · up to
          50 MB. Replacing with a different file type is allowed and is flagged for the reviewer.
          Editing a file means uploading a revised version — a reviewer compares it to the released
          file and e-signs to release the next version.
        </p>
      </div>
    </div>
  );
}
